
@if (!isLoading) {
  <div class="auth-page">
    <!-- ─── Left Brand Panel ─── -->
    <div class="auth-brand-panel">
      <img src="../../../assets/images/atlogo.png" class="brand-logo" alt="YouAttest">
      <h1 class="brand-tagline">Identity Governance<br>&amp; Administration</h1>
      <p class="brand-subtitle">Automate access certifications, enforce compliance, and reduce risk across your enterprise.</p>
      <ul class="brand-features">
        <li>Automate user access reviews &amp; campaigns</li>
        <li>Ensure regulatory compliance at scale</li>
        <li>Streamline reviewer workflows</li>
      </ul>
      <span class="brand-badge">Enterprise-grade security, SOC 2 certified</span>
    </div>
    <!-- ─── Right Form Panel ─── -->
    <div class="auth-form-panel">
      <!-- Step 1 & 2: Email + Password / OTP -->
      @if (!isNew) {
        <div class="auth-card" [@fadeInUp]>
          <img src="../../../assets/images/atlogo1.png" class="auth-logo" alt="YouAttest">
          <h2 class="auth-heading">{{ otpSent && isReviewer ? 'One-Time Password' : 'Welcome back' }}</h2>
          <p class="auth-subheading">
            @if (!user.userType) {
              Enter your email to continue
            }
            @if (user.userType && isAdmin) {
              Enter your password
            }
            @if (isReviewer && otpSent) {
              Check your email for the OTP
            }
          </p>
          <form class="auth-form" [formGroup]="loginForm"
            (ngSubmit)="!user.userType ? checkUserType() : userLogin()">
            <!-- Email -->
            <mat-form-field appearance="outline">
              <mat-label>Email Address</mat-label>
              <input matInput required placeholder="you@company.com"
                formControlName="email">
              @for (message of loginForm.get('email')['errorMessages']; track message) {
                <mat-error>
                  {{ message }}
                </mat-error>
              }
            </mat-form-field>
            <!-- Admin Password -->
            @if (otpSent && isAdmin) {
              <mat-form-field appearance="outline">
                <mat-label>Password</mat-label>
                <input matInput [type]="hide ? 'password' : 'text'" required
                  placeholder="Your password" formControlName="password">
                <button type="button" mat-icon-button matSuffix (click)="hide = !hide"
                  aria-label="Toggle password visibility">
                  <mat-icon>{{ hide ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                @if (isSubmitted) {
                  <mat-error>
                    @for (message of loginForm.get('password')['errorMessages']; track message) {
                      <span>{{ message }}</span>
                    }
                  </mat-error>
                }
              </mat-form-field>
            }
            <!-- Reviewer OTP -->
            @if (isReviewer) {
              <div class="auth-info-box">
                Thank you for using <strong>{{ applicationNameService.getApplicationName() }}</strong>.
                Please enter the One Time Password sent to your registered email.
              </div>
              <mat-form-field appearance="outline">
                <mat-label>One Time Password</mat-label>
                <input [type]="hideOtp ? 'password' : 'text'" matInput required
                  placeholder="Enter OTP" formControlName="password">
                <button type="button" mat-icon-button matSuffix (click)="hideOtp = !hideOtp"
                  aria-label="Toggle OTP visibility">
                  <mat-icon>{{ hideOtp ? 'visibility_off' : 'visibility' }}</mat-icon>
                </button>
                @if (isSubmitted) {
                  <mat-error>
                    @for (message of loginForm.get('password')['errorMessages']; track message) {
                      <span>{{ message }}</span>
                    }
                  </mat-error>
                }
              </mat-form-field>
              <div id="countdown" fxLayoutAlign="center stretch"></div>
              <button [disabled]="isButtonDisabled" type="button" mat-button
              class="auth-secondary-btn" (click)="checkUserType()">Resend OTP</button>
            }
            <!-- Forgot Password -->
            @if (isAdmin) {
              <div class="auth-link-row">
                <a [routerLink]="['/auth/forgot-password']">Forgot Password?</a>
              </div>
            }
            <!-- Submit -->
            <button type="submit" mat-raised-button class="auth-submit-btn">
              {{ user.userType ? 'Sign In' : 'Next' }}
            </button>
          </form>
        </div>
      }
      <!-- Phone Verification -->
      @if (isNew && factor.factorId == null) {
        <div class="auth-card" [@fadeInUp]>
          <img src="../../../assets/images/atlogo1.png" class="auth-logo" alt="YouAttest">
          <h2 class="auth-heading">Phone Verification</h2>
          <p class="auth-subheading">Add your phone number to secure your account. We'll send a verification code.</p>
          <form class="auth-form" [formGroup]="factorForm">
            <mat-form-field appearance="outline">
              <mat-label>Phone Number</mat-label>
              <input type="tel" matInput placeholder="+1XXXXXXXXXX"
                formControlName="phoneNumber">
            </mat-form-field>
            @if (isPhone) {
              <mat-error>
                @for (message of loginForm.get('phoneNumber')['errorMessages']; track message) {
                  <span>{{ message }}</span>
                }
              </mat-error>
            }
            <button mat-raised-button class="auth-submit-btn" type="submit" (click)="addFactor()">
              Verify Phone Number
            </button>
            <p class="auth-back-link" style="margin-top: 12px; font-size: 12px; color: #94a3b8;">
              Your number will not be shown publicly.
            </p>
          </form>
        </div>
      }
      <!-- OTP Activation -->
      @if (isActivate) {
        <div class="auth-card" [@fadeInUp]>
          <img src="../../../assets/images/atlogo1.png" class="auth-logo" alt="YouAttest">
          <h2 class="auth-heading">Enter Passcode</h2>
          <div class="auth-info-box">
            Thank you for using <strong>{{ applicationNameService.getApplicationName() }}</strong>.
            Please enter the One Time Password sent to your registered number
            <strong>{{ factor.phoneNumber }}</strong>.
          </div>
          <form class="auth-form" [formGroup]="factorForm">
            <mat-form-field appearance="outline">
              <mat-label>Enter Passcode</mat-label>
              <input matInput formControlName="passCode"
                placeholder="000000">
            </mat-form-field>
            @if (isPass) {
              <mat-error>
                @for (message of factorForm.get('passCode')['errorMessages']; track message) {
                  <span>{{ message }}</span>
                }
              </mat-error>
            }
            <button type="submit" mat-raised-button class="auth-submit-btn"
            (click)="activateFactor()">Verify &amp; Continue</button>
          </form>
        </div>
      }
      <!-- Copyright -->
      <p class="auth-copyright">
        <strong>Copyright</strong> &copy; {{ copyrightYear }} {{ applicationNameService.getApplicationName() }}.com
      </p>
    </div>
  </div>
}